import { IS_MOCK } from '@/env'
import { getLogger } from '@/logger'

const STORAGE_KEY = 'gallery-app-mock'

/** Set to true after the first localStorage error is logged to suppress duplicates. */
let storageErrorLogged = false

/**
 * Resolves which data source the gallery should use at runtime.
 *
 * Resolution order:
 * 1. Read `localStorage` key `gallery-app-mock`.
 *    - Value is trimmed of leading/trailing whitespace before evaluation.
 *    - Comparison is case-insensitive: `"true"` (any casing) enables mock source;
 *      `"false"` (any casing) forces BFF source.
 *    - Quote characters are **not** stripped; values such as `'"true"'` are invalid.
 *    - An empty string after trimming is treated as invalid.
 * 2. When the stored value is absent or invalid, fall back to the `VITE_USE_MOCK`
 *    environment variable (exposed as {@link IS_MOCK}).
 * 3. When `localStorage` access throws, the error is logged once per browser page
 *    lifetime (subsequent throws are silently suppressed) and the env flag is used.
 *
 * @returns `true` when mock data should be used, `false` when the BFF should be called.
 */
export function shouldUseMock(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null) {
      return IS_MOCK
    }

    const trimmed = raw.trim()
    if (trimmed === '') {
      return IS_MOCK
    }

    const lower = trimmed.toLowerCase()
    if (lower === 'true') {
      return true
    }
    if (lower === 'false') {
      return false
    }

    // Value present but not a recognised boolean — fall back to env.
    return IS_MOCK
  } catch (err) {
    if (!storageErrorLogged) {
      storageErrorLogged = true
      getLogger().error(`[gallery-data-source] localStorage access failed for key "${STORAGE_KEY}"`, err)
    }
    return IS_MOCK
  }
}

/**
 * Resets the one-time storage-error guard.
 *
 * Intended for use in tests only — do not call in production code.
 *
 * @internal
 */
export function _resetStorageErrorGuard(): void {
  storageErrorLogged = false
}
